import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Ambulance, Briefcase, School, Building, Trophy, ArrowRight } from "lucide-react";

const Residents = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) element.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[50vh] bg-black flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1541951930263-4ba77c427672?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center opacity-50" />
        <div className="relative container mx-auto px-4 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-white uppercase mb-4">Residents & Community</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Get involved, stay safe, and grow with Frog Lake First Nation.
          </p>
        </div>
      </div>

      {/* Moving to Frog Lake */}
      <section className="py-16 bg-[#D97706] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold uppercase mb-6">Moving to Frog Lake?</h2>
          <p className="text-lg max-w-3xl mx-auto mb-8">
            When you move here you'll get a Welcome Coupon delivered to your new home! Bring it to our office for a free Welcome Package with resources to get settled.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="secondary" className="rounded-none font-bold uppercase">View Community Directory</Button>
          </div>
        </div>
      </section>

      {/* Emergency Services */}
      <section id="emergency" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="bg-red-50 p-8 rounded-lg border border-red-100">
                <Ambulance className="h-12 w-12 text-red-600 mb-6" />
                <h2 className="text-3xl font-heading font-bold uppercase mb-4 text-red-700">Emergency Services</h2>
                <p className="text-gray-700 mb-6">
                  Frog Lake Fire & Rescue and EMS provide 24/7 coverage for the community.
                </p>
                <ul className="space-y-4 font-bold text-lg">
                  <li className="flex justify-between border-b pb-2">
                    <span>Emergency (Fire/Police/Amb)</span>
                    <span className="text-red-600">911</span>
                  </li>
                  <li className="flex justify-between border-b pb-2">
                    <span>Fire Hall (Non-Emergency)</span>
                    <span>780-943-2222</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Health Centre</span>
                    <span>780-943-3800</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="md:w-1/2 space-y-6">
              <h3 className="text-2xl font-bold uppercase">Community Safety</h3>
              <p className="text-gray-600">
                Our team works tirelessly to ensure the safety and well-being of all residents. We offer regular safety training, fire prevention education, and emergency preparedness workshops.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Employment */}
      <section id="employment" className="py-20 bg-zinc-50">
        <div className="container mx-auto px-4 text-center">
          <Briefcase className="h-12 w-12 text-[#D97706] mx-auto mb-4" />
          <h2 className="text-3xl font-heading font-bold uppercase mb-8">Employment Opportunities</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-12">
            Browse current job openings within the Nation administration and local businesses.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white p-6 border hover:border-[#D97706] transition-colors group cursor-pointer">
                <span className="text-xs font-bold text-gray-400 uppercase">Posted 2 days ago</span>
                <h3 className="font-bold text-lg mt-1 mb-2 group-hover:text-[#D97706] transition-colors">Administrative Assistant</h3>
                <p className="text-sm text-gray-600 mb-4">Frog Lake Administration Office</p>
                <div className="flex items-center text-sm font-bold text-[#D97706]">
                  Apply Now <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities & Rec */}
      <section id="facilities" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-heading font-bold uppercase">Facilities & Recreation</h2>
            <div className="h-1 w-20 bg-[#D97706] mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div id="recreation" className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <Trophy className="h-16 w-16 text-gray-400" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-xl uppercase mb-2">Recreation Centre</h3>
                <p className="text-gray-600 text-sm">
                  Featuring an ice arena, fitness gym, and community hall for events and sports.
                </p>
              </div>
            </div>
            
            <div id="schools" className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <School className="h-16 w-16 text-gray-400" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-xl uppercase mb-2">Schools</h3>
                <p className="text-gray-600 text-sm">
                  Chief Napeweaw Comprehensive School and Tus-Tuk-Ee-Skaws High School providing quality education.
                </p>
              </div>
            </div>

            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <Building className="h-16 w-16 text-gray-400" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-xl uppercase mb-2">Health Centre</h3>
                <p className="text-gray-600 text-sm">
                  Comprehensive health services including dental, nursing, and mental health support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Residents;
